import React, { useEffect, useState } from "react";
import { Col, Button, Row } from "reactstrap";
import { Colors,
    OutputRow,
    BoldLabel,
    LeftLabel,
    HighlightedCost,
    TableTitle,
    WhiteTableContainer,
    UnderlineButton,} from "../../../../utils/budget-tool-specific";
import { RebelColors } from "../../../../styles/rebel-experiences";

import AccommodationRow from "../../experiences/cost-summary/exp-accommodation-row";
import FlightRow from "../../experiences/cost-summary/exp-flight-row";
import ActivityRow from "../../experiences/cost-summary/exp-activity-row";
import TransportationRow from "../../experiences/cost-summary/exp-transportation-row";
import MealsRow from "../../experiences/cost-summary/exp-meals-row";
import MiscellaneousRow from "../../experiences/cost-summary/exp-miscellaneous-row";
import RebelFinalCostRow from './rebel-final-cost-row';
import RebelContingencyRow from "./rebel-contingency-row";
import { displayValue } from "../../helper";
import { calculateSubCostPerAttendee,
    calculateTotalCostPerAttendee,
    calculateCostTrip,
    calculateSubCostNoFlightsPerAttendee,
    calculateCostPerAttendee,
    calculateCostAccommodation,
    calculateCostFlight,
    calculateCostActivity,
    calculateCostGroundTransport,
    calculateCostMeals,
    calculateCostMeetings,
    calculateCostMiscellaneous,
    calculateRebelTotalCost,
    calculateRebelContingency,
    calculateInternalCostA,
    calculateInternalCostB } from "../../experiences/cost-summary/cost-helper";
import MeetingsRow from "../../experiences/cost-summary/exp-meetings.row";
import styled from "styled-components";

const CostOutput = ({guideData, expData, clientCurrency, currencyConversionRatio}) => {
    const attendees = parseFloat(guideData.attendees?.value) || 1;

    // Calculate margin cost (same logic as in rebel-final-cost-row)
    const calculateMarginCost = () => {
        if (guideData.marginPercent === undefined || guideData.marginPercent.value === true) {
            return calculateInternalCostB(guideData, expData, clientCurrency, currencyConversionRatio);
        } else {
            // Dollar-based margin - per person, multiplied by attendees
            return parseFloat(guideData.margin.value) * attendees;
        }
    };

    // Calculate total program cost using REBEL functions
    const rebelSubtotal = calculateRebelTotalCost(guideData, expData);
    const overheadCost = calculateInternalCostA(guideData, expData, clientCurrency, currencyConversionRatio);
    const marginCost = calculateMarginCost();
    const totalProgramCost = rebelSubtotal + overheadCost + marginCost;

    return (
        <React.Fragment>
            <RebelLeftLabel>Per Attendee Cost: </RebelLeftLabel> <RebelHighlightedCost>{guideData.currency.value} {displayValue(totalProgramCost / attendees)}</RebelHighlightedCost> <br/>
            <RebelLeftLabel>Total Program Cost: </RebelLeftLabel> <RebelHighlightedCost>{guideData.currency.value} {displayValue(totalProgramCost)}</RebelHighlightedCost> <br/> <br/>

            <RebelTableTitle>
                Trip Breakdown
            </RebelTableTitle>
            <RebelTableContainer id="output-form-container">
                <RebelOutputRow>
                    <Col lg="1">
                    </Col>
                    <Col>
                        <RebelBoldLabel>
                            Category
                        </RebelBoldLabel>
                    </Col>
                    <Col lg="2">
                        <RebelBoldLabel>
                            Per Attendee ({guideData.currency.value})
                        </RebelBoldLabel>
                    </Col>
                    <Col lg="1"></Col>
                    <Col lg="2">
                        <RebelBoldLabel>
                            Subtotal ({guideData.currency.value})
                        </RebelBoldLabel>
                    </Col>
                </RebelOutputRow>

                {calculateCostAccommodation(guideData, expData) > 0 && <AccommodationRow guideData={guideData} expData={expData}/>}
                {calculateCostFlight(guideData, expData) > 0 && <FlightRow guideData={guideData} expData={expData}/>}
                {calculateCostActivity(guideData, expData) > 0 && <ActivityRow guideData={guideData} expData={expData}/>}
                {calculateCostGroundTransport(guideData, expData) > 0 && <TransportationRow guideData={guideData} expData={expData}/>}
                {calculateCostMeals(guideData, expData) > 0 && <MealsRow guideData={guideData} expData={expData}/>}
                {calculateCostMeetings(guideData, expData) > 0 && <MeetingsRow guideData={guideData} expData={expData}/>}
                {calculateCostMiscellaneous(guideData, expData) > 0 && <MiscellaneousRow guideData={guideData} expData={expData}/>}
                {calculateRebelContingency(guideData, expData) > 0 && <RebelContingencyRow guideData={guideData} expData={expData}/>}
                <RebelFinalCostRow currencyConversionRatio={currencyConversionRatio} clientCurrency={clientCurrency} guideData={guideData} expData={expData} villaId={expData.villaId?.value}/>

                </RebelTableContainer>
            </React.Fragment>
    )
}

export default CostOutput;

const RebelLeftLabel = styled(LeftLabel)`
    color: ${RebelColors.text};
`

const RebelHighlightedCost = styled(HighlightedCost)`
    color: ${RebelColors.gold};
`

const RebelTableTitle = styled(TableTitle)`
    color: ${RebelColors.gold};
    border-bottom: none;
`

const RebelTableContainer = styled(WhiteTableContainer)`
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(31, 41, 55, 0.85) 50%, rgba(55, 65, 81, 0.85) 100%);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 12px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2);
    padding: 20px 20px 12px 20px;

    p {
        color: ${RebelColors.text};
    }

    span {
        color: ${RebelColors.text};
    }

    /* Style the "x {attendees}" text to white */
    > div > div {
        color: ${RebelColors.text};
    }

    /* Override all top-level OutputRow borders to be subtle gold, except no-border rows */
    > .row:not(.rebel-no-border) {
        border-bottom: 1px solid rgba(253, 181, 6, 0.2) !important;
    }

    /* Explicitly remove borders from no-border rows */
    > .row.rebel-no-border {
        border-bottom: none !important;
    }

    /* Remove borders from nested rows inside category rows (title row) */
    > .row .row {
        border-bottom: none !important;
    }

    /* Ensure rows with explicit border-bottom: none stay that way */
    > .row[style*="border-bottom: none"] {
        border-bottom: none !important;
    }

    /* Override CollapseRow borders in expanded sections to be subtle gold */
    > div > .row:not(.rebel-no-border) {
        border-bottom: 1px solid rgba(253, 181, 6, 0.2) !important;
    }

    /* Style the collapse button icons to gold */
    svg {
        color: ${RebelColors.gold};
    }

    /* Style the table icons to gold */
    .fa-bed, .fa-plane, .fa-running, .fa-car, .fa-utensils,
    .fa-briefcase, .fa-plus-circle, .fa-percentage {
        color: ${RebelColors.gold};
    }

    /* Override the yellow/gold collapse card backgrounds with premium styling */
    .card {
        background: linear-gradient(135deg, rgba(253, 181, 6, 0.1) 0%, rgba(253, 181, 6, 0.05) 50%, transparent 100%) !important;
        border: 1px solid rgba(253, 181, 6, 0.2);
        border-radius: 8px;
    }

    /* Ensure text in collapsed sections is white */
    .card-body {
        color: ${RebelColors.text};
    }

    .card-body p,
    .card-body span {
        color: ${RebelColors.text};
    }

    /* Remove extra bottom margin from last row for consistent spacing */
    > .row:last-child {
        margin-bottom: 0;
    }

    > .row:last-child p {
        margin-bottom: 0;
    }
`

const RebelOutputRow = styled(OutputRow)`
    border-bottom: 2px solid rgba(253, 181, 6, 0.4) !important;
`

const RebelBoldLabel = styled(BoldLabel)`
    color: ${RebelColors.gold};
`
